import * as React from 'react';

import { Time } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import type { VariantProps } from 'class-variance-authority';
import type { Locale } from 'date-fns';
import { format as formatDate, isValid, set as setDate } from 'date-fns';

import { Divider } from '../../lib/divider.jsx';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '../../lib/popover.jsx';
import { typographyVariants } from '../../lib/typography.jsx';
import { inputVariants, itemVariants } from '../../lib/variants.jsx';

type InputVariants = VariantProps<typeof inputVariants>;

type TimepickerProps = {
  value?: Date | null;
  defaultValue?: Date | null;
  onChange?: (time: Date | null) => void;
  minuteStep?: number;
  displayFormat?: string;
  use12Hour?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  className?: string;
  locale?: Locale;
  container?: React.ComponentProps<typeof PopoverPortal>['container'];
  isPortal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  popoverClassName?: string;
} & Pick<React.ComponentProps<'button'>, 'id' | 'name' | 'aria-label'> &
  InputVariants;

function clampStep(step: number | undefined) {
  if (!step || step <= 0) return 15;
  if (step > 60) return 60;
  return step;
}

function getStartOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
}

function formatTime(
  value: Date | null | undefined,
  displayFormat: string,
  locale?: Locale,
) {
  if (!value || !isValid(value)) return '';
  return formatDate(value, displayFormat, { locale });
}

function Timepicker({
  value,
  defaultValue = null,
  onChange,
  minuteStep: minuteStepProp = 1,
  displayFormat,
  use12Hour,
  placeholder = '00:00',
  readOnly = false,
  disabled = false,
  size = 'medium',
  color = 'default',
  className,
  id,
  name,
  'aria-label': ariaLabel,
  locale,
  container,
  isPortal,
  open,
  onOpenChange,
  popoverClassName,
}: TimepickerProps) {
  const minuteStep = React.useMemo(
    () => clampStep(minuteStepProp),
    [minuteStepProp],
  );
  const isOpenControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const openState = isOpenControlled ? open : internalOpen;
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isOpenControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isOpenControlled, onOpenChange],
  );
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState<Date | null>(
    defaultValue,
  );
  const [internalContainer, setInternalContainer] =
    React.useState<HTMLDivElement | null>(null);

  // Container that related to portal the popover content
  const setContainerRef = React.useCallback((node: HTMLDivElement | null) => {
    setInternalContainer((prev) => (prev === node ? prev : node));
  }, []);
  const effectiveContainer = container ?? internalContainer;
  const portalContainer = isPortal ? document.body : effectiveContainer;

  const selected = isControlled ? (value ?? null) : internalValue;

  // Detect whether the locale typically uses 12h time when prop is not explicitly provided
  const detected12h = React.useMemo(() => {
    if (!locale) return false;
    const thirteen = setDate(getStartOfToday(), { hours: 13 });
    const pmFull = formatDate(thirteen, 'p', { locale });
    const pmMeridiem = formatDate(thirteen, 'a', { locale });
    return pmFull.includes(pmMeridiem);
  }, [locale]);

  const is12Hour = use12Hour ?? detected12h;

  const effectiveDisplayFormat = React.useMemo(
    () => displayFormat ?? (is12Hour ? 'hh:mm a' : 'HH:mm'),
    [displayFormat, is12Hour],
  );

  const hoursOptions = React.useMemo(
    () =>
      is12Hour
        ? Array.from({ length: 12 }, (_, i) => i + 1)
        : Array.from({ length: 24 }, (_, i) => i),
    [is12Hour],
  );
  const minutesOptions = React.useMemo(() => {
    const mins: number[] = [];
    for (let m = 0; m < 60; m += minuteStep) mins.push(m);
    return mins;
  }, [minuteStep]);
  const amLabel = React.useMemo(
    () => formatDate(setDate(getStartOfToday(), { hours: 1 }), 'a', { locale }),
    [locale],
  );
  const pmLabel = React.useMemo(
    () =>
      formatDate(setDate(getStartOfToday(), { hours: 13 }), 'a', { locale }),
    [locale],
  );
  const periodOptions = React.useMemo(
    () => [
      { value: 'AM' as const, label: amLabel },
      { value: 'PM' as const, label: pmLabel },
    ],
    [amLabel, pmLabel],
  );

  function getBaseDate() {
    return selected ?? getStartOfToday();
  }

  function getSelectedHour24(): number {
    return getBaseDate().getHours();
  }

  function getSelectedHour12(): number {
    const h24 = getSelectedHour24();
    const h12 = h24 % 12;
    return h12 === 0 ? 12 : h12;
  }

  function getSelectedMinute(): number {
    return getBaseDate().getMinutes();
  }

  function getSelectedPeriod(): 'AM' | 'PM' {
    return getSelectedHour24() >= 12 ? 'PM' : 'AM';
  }

  function commit(next: Date) {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  }

  function handleSelectHour(hour: number) {
    const base = getBaseDate();
    let nextHour = hour;
    if (is12Hour) {
      const isPm = getSelectedPeriod() === 'PM';
      if (hour === 12) {
        nextHour = isPm ? 12 : 0;
      } else {
        nextHour = isPm ? hour + 12 : hour;
      }
    }
    const next = setDate(base, { hours: nextHour });
    commit(next);
  }

  function handleSelectMinute(minute: number) {
    const base = getBaseDate();
    const next = setDate(base, {
      minutes: minute,
      seconds: 0,
      milliseconds: 0,
    });
    commit(next);
  }

  function handleSelectPeriod(period: 'AM' | 'PM') {
    if (!is12Hour) return;
    const base = getBaseDate();
    const h24 = base.getHours();
    let nextHour = h24;
    const currentlyPm = h24 >= 12;
    if (period === 'AM' && currentlyPm) {
      nextHour = h24 === 12 ? 0 : h24 - 12;
    } else if (period === 'PM' && !currentlyPm) {
      nextHour = h24 === 0 ? 12 : h24 + 12;
    }
    const next = setDate(base, { hours: nextHour });
    commit(next);
  }

  return (
    <Popover open={openState} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          name={name}
          aria-label={ariaLabel}
          disabled={disabled}
          data-size={size}
          data-color={color}
          className={cn(
            'group/timepicker-trigger -[1fr_16px] grid min-w-26 cursor-pointer grid-cols-[1fr_16px] items-center gap-1 rounded-sm border border-border-primary bg-ui-field02 fill-icon-secondary px-3 text-left text-text-primary read-only:bg-ui-field02 data-[size=medium]:h-10 data-[size=small]:h-8',
            //focus state
            'hover:border-action-focusBorder focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-action-focusBorder',
            //placeholder state
            'data-placeholder:text-text-placeholder',
            //warning color variant
            'data-[color=warning]:border-2 data-[color=warning]:border-warning-main data-[color=warning]:focus-visible:outline-none',
            //error color variant
            'data-[color=error]:border-2 data-[color=error]:border-error-main data-[color=error]:focus-visible:outline-none',
            //disabled state
            'disabled:pointer-events-none disabled:border-border-disabled disabled:bg-action-disabledBackground disabled:fill-text-disabled disabled:text-text-disabled',
            //readOnly state
            readOnly &&
              'pointer-events-none bg-ui-field01 text-text-secondary focus-visible:border focus-visible:border-border-primary focus-visible:outline-none',
            className,
          )}
        >
          <span
            className={cn(
              !selected && 'text-text-placeholder',
              'w-16 text-left text-nowrap',
              typographyVariants({
                variant: 'bodyShort01',
              }),
            )}
          >
            {selected
              ? formatTime(selected, effectiveDisplayFormat, locale)
              : placeholder}
          </span>
          <span>
            <Time
              data-hidden={readOnly}
              className="h-4 w-4 data-[hidden=true]:hidden"
            />
          </span>
        </button>
      </PopoverTrigger>
      {portalContainer && (
        <PopoverPortal container={portalContainer}>
          <PopoverContent
            align="start"
            sideOffset={4}
            className={cn('overflow-hidden', popoverClassName)}
          >
            <div className={cn('flex h-56')}>
              <ul className="max-h-56 w-fit scrollbar-none overflow-auto [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {hoursOptions.map((h) => {
                  const isSelected = is12Hour
                    ? getSelectedHour12() === h
                    : getSelectedHour24() === h;
                  const label = String(h).padStart(2, '0');

                  return (
                    <li key={`h-${h}`}>
                      <button
                        type="button"
                        data-selected={isSelected}
                        className={cn(
                          itemVariants({
                            popoverItem: isSelected ? 'selected' : 'default',
                          }),
                          typographyVariants({
                            variant: 'bodyShort01',
                          }),
                          'w-full focus-visible:bg-ui-hover focus-visible:outline-none data-[selected=true]:focus-visible:bg-ui-highlight',
                        )}
                        onClick={() => handleSelectHour(h)}
                      >
                        {label}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <Divider orientation="vertical" className="max-h-56" />

              <ul
                tabIndex={-1}
                className="max-h-64 w-fit scrollbar-none overflow-auto [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                {minutesOptions.map((m) => {
                  const isSelected = getSelectedMinute() === m;
                  const label = String(m).padStart(2, '0');
                  return (
                    <li key={`m-${m}`}>
                      <button
                        type="button"
                        data-selected={isSelected}
                        className={cn(
                          itemVariants({
                            popoverItem: isSelected ? 'selected' : 'default',
                          }),
                          typographyVariants({
                            variant: 'bodyShort01',
                          }),
                          'w-full focus-visible:bg-ui-hover focus-visible:outline-none data-[selected=true]:focus-visible:bg-ui-highlight',
                        )}
                        onClick={() => handleSelectMinute(m)}
                      >
                        {label}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {is12Hour && (
                <Divider orientation="vertical" className="max-h-56" />
              )}
              {is12Hour && (
                <ul className="max-h-64 w-fit scrollbar-none overflow-auto [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  {periodOptions.map((p) => {
                    const isSelected = getSelectedPeriod() === p.value;
                    return (
                      <li key={`p-${p.value}`}>
                        <button
                          type="button"
                          data-selected={isSelected}
                          className={cn(
                            itemVariants({
                              popoverItem: isSelected ? 'selected' : 'default',
                            }),
                            typographyVariants({
                              variant: 'bodyShort01',
                            }),
                            'w-full focus-visible:bg-ui-hover focus-visible:outline-none data-[selected=true]:focus-visible:bg-ui-highlight',
                          )}
                          onClick={() => handleSelectPeriod(p.value)}
                        >
                          {p.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </PopoverContent>
        </PopoverPortal>
      )}
      <div ref={setContainerRef} />
    </Popover>
  );
}

export default Timepicker;
